import { ChevronDownIcon } from "@heroicons/react/solid";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContentText,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { CryptoIcon } from "src/Common";
import { ExchangeIcon } from "src/Common/components/ExchangeIcon";
import { useMarkAsReadCrypto } from "src/Modules/Arbitrage/context";
import { ReadReason } from "src/Modules/Arbitrage/types";
import { Controller, useWatch } from "react-hook-form";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { toast } from "react-toastify";

const MarkAsReadCryptoContent = () => {
  const { form, onSubmit, createMutation, isEditMode } = useMarkAsReadCrypto();
  const {
    control,
    formState: { errors },
  } = form;

  const quoteCoinOptions =
    useWatch({
      control,
      name: "quoteCoin",
    }) ?? "";

  const readReason = useWatch({
    control,
    name: "reason",
  });

  const address = useWatch({
    control,
    name: "address",
  });

  const handleCopyAddress = (val: string) => {
    navigator.clipboard.writeText(val).then(() => {
      toast("Address copied to clipboard", {
        theme: "dark",
        type: "success",
        autoClose: 8000,
        pauseOnFocusLoss: false,
      });
    });
  };

  return (
    <>
      <Typography
        variant="body1"
        style={{
          fontSize: "1rem",
          padding: "1rem 2rem",
          opacity: 0.7,
          borderBottom: "0.1px solid #393939",
        }}
      >
        Mark as read
      </Typography>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogContentText style={{ padding: "2rem" }}>
          <InputLabel style={{ fontSize: "0.8rem" }}>
            Crypto
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb="1rem"
              pt="0.5rem"
            >
              <Box display="flex" alignItems="center">
                <CryptoIcon
                  style={{ height: "1.5rem", marginRight: "0.5rem" }}
                  symbol={form.getValues("name")}
                />
                <Typography variant="body1">
                  {form.getValues("name")}
                </Typography>
              </Box>

              <ChevronDownIcon height="1.5rem" />
            </Box>
          </InputLabel>

          <InputLabel style={{ fontSize: "0.8rem" }}>
            Exchange
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              pt="0.5rem"
              mb="1rem"
            >
              <Box display="flex" alignItems="center">
                <ExchangeIcon
                  style={{ height: "2rem" }}
                  name={form.getValues("exchange")}
                />
                <Typography variant="body1">
                  {form.getValues("exchange")}
                </Typography>
              </Box>
              <ChevronDownIcon height="1.5rem" />
            </Box>
          </InputLabel>

          {!!address && (
            <TextField
              style={{ marginBottom: "1rem" }}
              label="address"
              disabled
              defaultValue={address}
              id="address-input"
              fullWidth
              helperText={errors?.address?.message}
              {...form.register("address")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={!address}
                      aria-label="copy address"
                      onClick={() => handleCopyAddress(address ?? "")}
                      edge="end"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <FormControl
            style={{ fontSize: "0.8rem", marginBottom: "1rem" }}
            fullWidth
            error={!!errors.quoteCoin}
          >
            <TextField
              select
              id="quoteCoin-select"
              fullWidth
              label="Quote Coin"
              defaultValue={form.getValues("quoteCoin") ?? ""}
              {...form.register("quoteCoin")}
            >
              {!!quoteCoinOptions.length && (
                <MenuItem
                  style={{ display: "flex", alignItems: "center" }}
                  value={quoteCoinOptions}
                >
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <CryptoIcon
                      style={{ height: "1.5rem", marginRight: "0.3rem" }}
                      symbol={quoteCoinOptions}
                    />
                    <span>{quoteCoinOptions}</span>
                  </Box>
                </MenuItem>
              )}
              <MenuItem value="">None</MenuItem>
            </TextField>
            <FormHelperText>{errors?.quoteCoin?.message}</FormHelperText>
          </FormControl>

          <FormControl
            style={{ fontSize: "0.8rem", marginBottom: "1rem" }}
            fullWidth
            error={!!errors.endDate}
          >
            <Controller
              control={form.control}
              name="endDate"
              render={({ field }) => {
                return (
                  <DateTimePicker
                    ampm
                    label="End Date"
                    renderInput={(params) => <TextField {...params} />}
                    {...field}
                  />
                );
              }}
            />
            <FormHelperText>{errors?.endDate?.message}</FormHelperText>
          </FormControl>

          <TextField
            select
            label="Reason"
            defaultValue={form.getValues("reason") ?? ReadReason.LOW_LIQUIDITY}
            id="reason-select"
            fullWidth
            helperText={errors?.reason?.message}
            {...form.register("reason")}
          >
            {Object.values(ReadReason).map((reason) => (
              <MenuItem key={reason} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </TextField>
          {readReason === ReadReason.LOW_LIQUIDITY && (
            <TextField
              style={{ marginTop: "1rem" }}
              label="Remove Mark as read at volume:"
              type="number"
              fullWidth
              helperText={errors?.volumeToRemoveMarkAsRead?.message}
              {...form.register("volumeToRemoveMarkAsRead")}
            />
          )}
        </DialogContentText>

        <DialogActions
          style={{ padding: "2rem", borderTop: "0.1px solid #393939" }}
        >
          <Button
            disabled={createMutation.isLoading || isEditMode}
            variant="contained"
            color="primary"
            fullWidth
            type="submit"
          >
            {createMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              `${isEditMode ? "Update " : ""}Mark as Read`
            )}
          </Button>
        </DialogActions>
      </form>
    </>
  );
};

/* This Dialog wrapper is necessary 
to avoid permanent render, 
without compromising opening dialog animation */

export const MarkAsReadCryptoDialog = () => {
  const { isOpen, onClose } = useMarkAsReadCrypto();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      {isOpen && <MarkAsReadCryptoContent />}
    </Dialog>
  );
};
